import { useState } from 'react';
import { Undo2, Wrench } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { localeUI } from '../lib/format';
import { CAMPOS_CIFRA, type CampoCifra, type LecturaCompleta, type Resolucion, fmtMoney } from '../lib/estimateReading';

/**
 * RESOLVER (mig 0510): el cuadro de la pestaña Checks para arreglar a mano lo que el lector dejó mal.
 *
 * Pedido del dueño (2026-10-13): "cuando el lector hace el parse y me coloca el semáforo en rojo o en amarillo, yo
 * solamente veo lo que está incorrecto, pero no me deja como darle solución… José Cervantes… no leyó un monto de
 * 5.000 y algo de building codes, y eso va al total". Cuatro acciones: agregar un bloque de cobertura que no sumó,
 * agregar una partida que se saltó, corregir un total, o aceptar la lectura tal cual con el porqué. El servidor
 * aplica en código, vuelve a validar y devuelve la lectura entera: acá solo se pinta lo que vuelve.
 */
type Kind = Resolucion['kind'];
const KINDS: Kind[] = ['add_block', 'add_line', 'set_figure', 'accept'];

/** "$1,234.50" / "1234,5" → número; vacío o basura → null. */
function num(s: string | undefined): number | null {
  const t = (s ?? '').replace(/[$\s]/g, '').replace(/,(?=\d{3}(\D|$))/g, '').replace(',', '.');
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

export function EstimateReadingResolver({ fileId, data, onApplied }: { fileId: string; data: LecturaCompleta; onApplied: (d: LecturaCompleta) => void }) {
  const { t } = useI18n();
  const [kind, setKind] = useState<Kind>('add_block');
  const [f, setF] = useState<Record<string, string>>({});
  const [pwi, setPwi] = useState(true);
  const [addToTotals, setAddToTotals] = useState(true);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const v = (k: string) => f[k] ?? '';
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setF((o) => ({ ...o, [k]: e.target.value }));
  const resoluciones = data.resolutions ?? [];

  /** El cuerpo para el servidor, o el mensaje de lo que falta. */
  function cuerpo(): Record<string, unknown> | string {
    const page = num(v('page'));
    const base = { note: note.trim(), page: page != null && page > 0 ? Math.round(page) : null };
    if (kind === 'add_block') {
      const amount = num(v('amount'));
      if (!v('label').trim() || amount == null || amount <= 0) return t('rd.rs.needLabelAmount');
      return { kind, label: v('label').trim(), amount, paid_when_incurred: pwi, acv: pwi ? null : num(v('acv')), depreciation: pwi ? null : num(v('dep')), ...base };
    }
    if (kind === 'add_line') {
      const rcv = num(v('rcv'));
      if (!v('desc').trim() || rcv == null) return t('rd.rs.needDescRcv');
      const [cat, sel, act] = v('code').trim().split(/\s+/);
      return {
        kind, desc: v('desc').trim(), structure: v('structure').trim() || null, area: v('area').trim() || null, cat: cat || null, sel: sel || null, act: act || null,
        qty: num(v('qty')), unit: v('unit').trim() || null, up: num(v('up')), tax: num(v('tax')), op: num(v('op')), rcv, dep: num(v('dep')), acv: null, add_to_totals: addToTotals, ...base,
      };
    }
    if (kind === 'set_figure') return { kind, field: (v('field') || 'rcv') as CampoCifra, value: num(v('value')), ...base };
    if (!note.trim()) return t('rd.rs.needNote');
    return { kind: 'accept', note: note.trim() };
  }

  const mensajeDe = (e: unknown) => t('rd.rs.err', { msg: e instanceof ApiError ? (e.message || e.code || String(e.status)) : String(e) });

  async function aplicar() {
    const b = cuerpo();
    if (typeof b === 'string') { setErr(b); return; }
    setBusy(true); setErr(null); setOk(null);
    try {
      const d = await api.post<LecturaCompleta>(`/api/files/${fileId}/reading/resolutions`, b);
      onApplied(d);
      const fs = d.reading.financial_summary;
      setOk(t('rd.rs.result', { sema: t(`rd.sema.${d.validation.semaforo}`), rcv: fmtMoney(fs.rcv.value ?? fs.grand_total.value), acv: fmtMoney(fs.acv.value) }));
      setF({}); setNote('');
    } catch (e) { setErr(mensajeDe(e)); } finally { setBusy(false); }
  }

  async function deshacer(rid: string) {
    setBusy(true); setErr(null); setOk(null);
    try { onApplied(await api.del<LecturaCompleta>(`/api/files/${fileId}/reading/resolutions/${rid}`)); } catch (e) { setErr(mensajeDe(e)); } finally { setBusy(false); }
  }

  /** Una línea legible por resolución aplicada. */
  function resumen(r: Resolucion): string {
    switch (r.kind) {
      case 'add_block': return `${r.label} · ${fmtMoney(r.amount)}${r.paid_when_incurred ? ' · PWI' : ''}`;
      case 'add_line': return `${r.desc} · ${fmtMoney(r.rcv)}${r.add_to_totals ? '' : ` · ${t('rd.rs.notInTotals')}`}`;
      case 'set_figure': return `${t(`rd.tot.${r.field}`)} → ${r.value == null ? '—' : fmtMoney(r.value)}`;
      case 'accept': return r.note;
    }
  }

  const campo = (k: string, label: string, extra?: Partial<React.InputHTMLAttributes<HTMLInputElement>> & { wide?: boolean }) => (
    <label className={`rd-rs-field${extra?.wide ? ' wide' : ''}`}>
      <span>{label}</span>
      <input className="input sm" value={v(k)} onChange={set(k)} disabled={busy} placeholder={extra?.placeholder} inputMode={extra?.inputMode} />
    </label>
  );

  return (
    <section className="rd-rs" aria-label={t('rd.rs.title')}>
      <div className="rd-rs-head"><Wrench size={14} strokeWidth={1.75} style={{ color: 'var(--accent)' }} /> {t('rd.rs.title')}</div>
      <p className="rd-rs-intro">{t('rd.rs.intro')}</p>
      <label className="rd-rs-field wide">
        <span>{t('rd.rs.action')}</span>
        <select className="input sm" value={kind} onChange={(e) => { setKind(e.target.value as Kind); setErr(null); setOk(null); }} disabled={busy}>
          {KINDS.map((k) => <option key={k} value={k}>{t(`rd.rs.k.${k}`)}</option>)}
        </select>
      </label>
      {kind === 'add_block' && (
        <>
          <div className="rd-rs-grid">
            {campo('label', t('rd.rs.label'), { placeholder: t('rd.rs.labelPh'), wide: true })}
            {campo('amount', t('rd.rs.amount'), { inputMode: 'decimal', placeholder: '5,604.35' })}
            {!pwi && campo('acv', t('rd.rs.blockAcv'), { inputMode: 'decimal' })}
            {!pwi && campo('dep', t('rd.rs.blockDep'), { inputMode: 'decimal' })}
            {campo('page', t('rd.rs.page'), { inputMode: 'numeric' })}
          </div>
          <label className="rd-check"><input type="checkbox" checked={pwi} onChange={(e) => setPwi(e.target.checked)} disabled={busy} /> {t('rd.rs.pwi')}</label>
        </>
      )}
      {kind === 'add_line' && (
        <>
          <div className="rd-rs-grid">
            {campo('desc', t('rd.rs.desc'), { wide: true })}
            {campo('structure', t('rd.rs.zone'))}
            {campo('area', t('rd.rs.area'))}
            {campo('code', t('rd.rs.code'), { placeholder: 'RFG 300 +' })}
            {campo('qty', t('rd.rs.qty'), { inputMode: 'decimal' })}
            {campo('unit', t('rd.rs.unit'), { placeholder: 'SQ' })}
            {campo('up', t('rd.rs.up'), { inputMode: 'decimal' })}
            {campo('tax', t('rd.rs.tax'), { inputMode: 'decimal' })}
            {campo('op', 'O&P', { inputMode: 'decimal' })}
            {campo('rcv', t('rd.rs.rcv'), { inputMode: 'decimal' })}
            {campo('dep', t('rd.rs.dep'), { inputMode: 'decimal' })}
            {campo('page', t('rd.rs.page'), { inputMode: 'numeric' })}
          </div>
          <label className="rd-check"><input type="checkbox" checked={addToTotals} onChange={(e) => setAddToTotals(e.target.checked)} disabled={busy} /> {t('rd.rs.addToTotals')}</label>
        </>
      )}
      {kind === 'set_figure' && (
        <div className="rd-rs-grid">
          <label className="rd-rs-field">
            <span>{t('rd.rs.field')}</span>
            <select className="input sm" value={v('field') || 'rcv'} onChange={set('field')} disabled={busy}>
              {CAMPOS_CIFRA.map((k) => <option key={k} value={k}>{t(`rd.tot.${k}`)} ({fmtMoney(data.reading.financial_summary[k]?.value)})</option>)}
            </select>
          </label>
          {campo('value', t('rd.rs.value'), { inputMode: 'decimal' })}
          {campo('page', t('rd.rs.page'), { inputMode: 'numeric' })}
        </div>
      )}
      <label className="rd-rs-field wide">
        <span>{t('rd.rs.note')}</span>
        <input className="input sm" value={note} onChange={(e) => setNote(e.target.value)} disabled={busy} placeholder={t('rd.rs.notePh')} />
      </label>
      <div className="rd-rs-actions">
        <button type="button" className="btn primary sm" onClick={() => void aplicar()} disabled={busy}>{busy ? t('rd.rs.applying') : t('rd.rs.apply')}</button>
        {err && <span className="rd-rs-err" role="alert">{err}</span>}
        {ok && !err && <span className="rd-rs-ok" role="status">{ok}</span>}
      </div>
      <h4 className="rd-bloque-t" style={{ marginTop: 4 }}>{t('rd.rs.applied', { n: resoluciones.length })}</h4>
      {resoluciones.length === 0 ? <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('rd.rs.none')}</span> : (
        <div className="rd-rs-list">
          {resoluciones.map((r) => (
            <div key={r.id} className="rd-rs-item">
              <span className="badge slate" style={{ fontSize: 'var(--fs-2xs)' }}>{t(`rd.rs.short.${r.kind}`)}</span>
              <span>
                {resumen(r)}
                {r.kind !== 'accept' && r.note ? <span className="muted"> — {r.note}</span> : null}
                <span className="who"> · {r.by_name} · {new Date(r.at).toLocaleString(localeUI(), { dateStyle: 'medium', timeStyle: 'short' })}</span>
              </span>
              <button type="button" className="btn ghost sm" onClick={() => void deshacer(r.id)} disabled={busy} title={t('rd.rs.undo')}><Undo2 size={12} strokeWidth={1.75} /> {t('rd.rs.undo')}</button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
